import {
  Box,
  ClickAwayListener,
  Drawer,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  ThemeProvider,
  Toolbar,
  createTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { CustomerBuyerOffersState, Offer, OffersQueryParameter } from "../../shared/models/customerMyOffersModel";
import ActionToast from "../../../shared/components/ActionToast";
import Loader from "../../../shared/components/Loader";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import Table from "../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import ImageBox from "../../../shared/components/ImageBox";
import { productCondition } from "../../../common/models/CommonModels";
import { NumericFormat } from "react-number-format";
import Moment from "react-moment";
import colors from "../../../shared/themes/colors";
import CartIcon from "../../../shared/assets/icons/CartIcon";
import { changeQuery, getDocument, getOffers, selectOffer, setError, setSuccess } from "../customerReceivedOffersSilce";
import TablePagination from "../../../shared/components/tables/TablePagination";
import { NotificationMessage, formatWarrantyFullName } from "../../../shared/models/models";
import OffersReceivedDetails from "./OffersReceivedDetails";
import WarningMessageIcon from "../../../shared/assets/icons/WarningMessageIcon";
import DownloadFile, { CustomTooltip } from "../../../shared/components/DownloadFile";
import useTranslation from "../../../shared/hooks/useTranslation";

const OffersReceived = () => {
  const { query, offers, offer, loading, success, error } = useAppSelector<CustomerBuyerOffersState>(
    (state: any) => state.customerReceivedOffers
  );

  const dispatch = useAppDispatch();

  const columns: TableColumnDefinition[] = [
    { title: undefined, twoLevelsColumWidth: "40px" },
    { title: "Image", twoLevelsColumWidth: "72px" },
    { title: "Company details" },
    { title: "Price/pc, quantity", twoLevelsColumWidth: "135px" },
    { title: "Condition" },
    // { title: "Shipping" },
    { title: "My RFQ details", parameter: OffersQueryParameter.createdDate },
    { title: "Offer", columnAlign: "center" },
    { title: undefined }
  ];

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const [offersDetails, setOffersDetails] = useState(false);

  const handleOfferSelect = (item: Offer) => {
    dispatch(selectOffer(item));
    setOffersDetails(true);
  };

  const init = async () => {
    await dispatch(getOffers(query));
  };

  const handleClickAway = () => {
    dispatch(selectOffer(null));
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9"
          }
        }
      }
    }
  });

  const { translate } = useTranslation("common");

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between">
        <Box className="heading-2">Offers Received</Box>
      </Toolbar>

      <Box className="px-4 v-fill">
        {loading ? (
          <Loader />
        ) : !offers?.items.length ? (
          <TableEmpty iconComponent={BoxSearchIcon} text="You have no offers yet"></TableEmpty>
        ) : (
          <Table className="px-4">
            <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

            <TableBody>
              {offers?.items.map((item) => {
                return (
                  <TableRow
                    key={item.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOfferSelect(item);
                    }}
                    selected={item.id === offer?.id}
                  >
                    <TableCell>
                      {item?.isPriceBoundToDocument && (
                        <CustomTooltip
                          title={
                            <Box className="text-12-bold" color={colors.primary.white1} sx={{ width: "150px" }}>
                              Price is bound to terms and conditions in PDF
                            </Box>
                          }
                          arrow
                          placement="right"
                        >
                          <Box component="button" onMouseMove={(event) => event.stopPropagation()}>
                            <WarningMessageIcon />
                          </Box>
                        </CustomTooltip>
                      )}
                    </TableCell>

                    <TableCell className="break-all" sx={{ maxWidth: "250px" }}>
                      <ImageBox size="small" src={item.imageFile?.content} />
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-bold" color={"#333333"}>
                        {item.supplier.name}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-18-medium" color={"#333333"}>
                        <NumericFormat
                          value={item.unitPrice?.value}
                          displayType="text"
                          thousandSeparator=" "
                          suffix="€"
                        />
                        <Box className="text-14-medium" color={"#333333"}>
                          {item.quantity}pcs
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={"#333333"}>
                        {translate(`productCondition.${productCondition(item.productConditionCode)}`)}
                      </Box>
                      <Box className="text-12-regular" color={"#333333"}>
                        {formatWarrantyFullName(item.warrantyPeriodMonths)}
                      </Box>
                    </TableCell>

                    {/* <TableCell>Shipping</TableCell> */}

                    <TableCell>
                      <Box>
                        <Box className="text-12-regular" color={colors.secondary.gray40}>
                          Created on{"\u00A0"}
                          <Moment date={item.createdDate} format="DD.MM.YYYY" />
                        </Box>

                        <Box className="text-12-regular" color={colors.secondary.gray40}>
                          {"#" + item.quotationRequestNumber}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <DownloadFile item={item} title="Download offer in .pdf" request={getDocument} />
                    </TableCell>

                    <TableCell>
                      <IconButton>
                        <CartIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={offers?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {offer && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div className="absolute">
            <Drawer
              variant="persistent"
              anchor="right"
              open={offersDetails}
              onClose={() => setOffersDetails(false)}
              hideBackdrop
              elevation={0}
              PaperProps={{
                sx: {
                  background: "#FFF !important",
                  borderLeft: "1px solid #D5D6D9",
                  marginTop: "150px",
                  height: "calc(100% - 150px)"
                }
              }}
            >
              <OffersReceivedDetails />
            </Drawer>
          </div>
        </ClickAwayListener>
      )}
    </ThemeProvider>
  );
};
export default OffersReceived;
